import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, BACKEND_URL } from '../../firebase/config';
import { collection, query, getDocs, doc, getDoc, setDoc, updateDoc, addDoc, where, Timestamp, deleteDoc, orderBy } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { signOut } from 'firebase/auth';
import { 
  Bell, LogOut, Search, Users, UserPlus, TrendingUp, Award, AlertTriangle, 
  BarChart3, FileText, Send, Edit, Eye, X, ExternalLink, Grid, Mail, User, Trash2, Camera 
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { createStudentViewPath } from '../../utils/studentRoutes';

function HODDashboard() {
  const navigate = useNavigate();
  const [hod, setHod] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('roll');
  const [searchedStudent, setSearchedStudent] = useState(null);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showSheetsModal, setShowSheetsModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [showStaffProfileModal, setShowStaffProfileModal] = useState(false);
  const [showEditStaffModal, setShowEditStaffModal] = useState(false);
  const [showHODProfileModal, setShowHODProfileModal] = useState(false);
  const [showSentMessages, setShowSentMessages] = useState(false);
  const [activeSheetTab, setActiveSheetTab] = useState('attendance');
  const [selectedStaffProfile, setSelectedStaffProfile] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingHOD, setEditingHOD] = useState(null);
  const [sentMessages, setSentMessages] = useState([]);
  
  const [newStaff, setNewStaff] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    mobile: '',
    qualifications: '',
    photoUrl: '',
    assignedBatch: '',
    assignedDivision: ''
  });
  
  const [assignmentMode, setAssignmentMode] = useState('individual');
  const [assignmentType, setAssignmentType] = useState('student');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [assignmentData, setAssignmentData] = useState({
    division: '',
    batch: '',
    mentorEmail: '',
    classCoordinator: ''
  });
  
  const [message, setMessage] = useState({
    recipient: '',
    recipientType: 'student',
    messageType: 'reminder',
    text: ''
  });
  
  const [stats, setStats] = useState({
    totalStudents: 0,
    avgAttendance: 0,
    atRisk: 0,
    totalAchievements: 0
  });

  useEffect(() => {
    loadHODData();
  }, []);

  const loadHODData = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate('/staff-login');
      return;
    }

    const hodDoc = await getDoc(doc(db, 'users', currentUser.email));
    if (hodDoc.exists()) {
      setHod(hodDoc.data());

      const studentsSnapshot = await getDocs(collection(db, 'students'));
      const studentsData = [];
      let totalAttendance = 0;
      let atRiskCount = 0;

      for (const studentDoc of studentsSnapshot.docs) {
        const studentData = studentDoc.data();
        
        const attendanceDoc = await getDoc(doc(db, 'attendance', studentData.rollNumber));
        const attendanceData = attendanceDoc.exists() ? attendanceDoc.data() : null;
        
        if (attendanceData) {
          totalAttendance += attendanceData.overallPercentage || 0;
          if (attendanceData.overallPercentage < 75) atRiskCount++;
        }

        studentsData.push({
          ...studentData,
          attendance: attendanceData
        });
      }

      setAllStudents(studentsData);

      const staffSnapshot = await getDocs(collection(db, 'users'));
      const staffData = staffSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllStaff(staffData);

      const achievementsSnapshot = await getDocs(collection(db, 'achievements'));
      const verifiedCount = achievementsSnapshot.docs.filter(
        doc => doc.data().status === 'verified'
      ).length;

      setStats({
        totalStudents: studentsData.length,
        avgAttendance: studentsData.length > 0 ? Math.round(totalAttendance / studentsData.length) : 0,
        atRisk: atRiskCount,
        totalAchievements: verifiedCount
      });

      // Load sent messages
      try {
        console.log('🔍 Loading sent messages for:', currentUser.email);
        const sentMessagesQuery = query(
          collection(db, 'sentMessages'),
          where('fromEmail', '==', currentUser.email)
        );
        const sentSnapshot = await getDocs(sentMessagesQuery);
        console.log('📨 Found sent messages:', sentSnapshot.docs.length);
        
        const sentData = sentSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually by createdAt
        sentData.sort((a, b) => {
          const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
          const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
          return timeB - timeA;
        });
        
        console.log('✅ Loaded sent messages:', sentData);
        setSentMessages(sentData);
      } catch (error) {
        console.error('❌ Error loading sent messages:', error);
      }
    }

    setLoading(false);
  } catch (error) {
    console.error('Error loading HOD data:', error);
    setLoading(false);
  }
};

  const handleSearchStudent = async (selectedStudent = null) => {
    try {
      let foundStudent = selectedStudent;

      if (!foundStudent) {
        if (searchMode === 'roll') {
          if (!searchQuery) return;
          const studentDoc = await getDoc(doc(db, 'students', searchQuery.toUpperCase()));
          if (studentDoc.exists()) {
            foundStudent = { ...studentDoc.data(), rollNumber: studentDoc.id };
          }
        } else {
          const lowerQuery = searchQuery.toLowerCase();
          foundStudent = allStudents.find(s => 
            s.firstName?.toLowerCase() === lowerQuery || 
            s.surname?.toLowerCase() === lowerQuery ||
            `${s.firstName} ${s.surname}`.toLowerCase() === lowerQuery
          );
        }
      }

      if (foundStudent) {
        const attendanceDoc = await getDoc(doc(db, 'attendance', foundStudent.rollNumber));
        const attendanceData = attendanceDoc.exists() ? attendanceDoc.data() : null;

        const iaTest1Doc = await getDoc(doc(db, 'iaTest1', foundStudent.rollNumber));
        const iaTest2Doc = await getDoc(doc(db, 'iaTest2', foundStudent.rollNumber));

        const achievementsQuery = query(collection(db, 'achievements'));
        const achievementsSnapshot = await getDocs(achievementsQuery);
        const studentAchievements = achievementsSnapshot.docs
          .filter(doc => doc.data().rollNumber === foundStudent.rollNumber)
          .map(doc => ({ id: doc.id, ...doc.data() }));

        setSearchedStudent({
          ...foundStudent,
          attendance: attendanceData,
          iaTest1: iaTest1Doc.exists() ? iaTest1Doc.data() : null,
          iaTest2: iaTest2Doc.exists() ? iaTest2Doc.data() : null,
          achievements: studentAchievements
        });
        
        setShowSuggestions(false);
        setSearchSuggestions([]);
      } else {
        alert('Student not found');
        setSearchedStudent(null);
      }
    } catch (error) {
      console.error('Error searching student:', error);
      alert('Error searching student');
    }
  };

  const handleSearchInputChange = (value) => {
    setSearchQuery(value);
    
    if (searchMode === 'name' && value.length >= 2) {
      const lowerQuery = value.toLowerCase();
      const matches = allStudents.filter(s => 
        s.firstName?.toLowerCase().includes(lowerQuery) || 
        s.surname?.toLowerCase().includes(lowerQuery) ||
        `${s.firstName} ${s.surname}`.toLowerCase().includes(lowerQuery)
      ).slice(0, 10);
      
      setSearchSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleUpdateHODProfile = async (e) => {
    e.preventDefault();

    try {
      const hodRef = doc(db, 'users', hod.email);
      await updateDoc(hodRef, {
        name: editingHOD.name,
        mobile: editingHOD.mobile,
        photoUrl: editingHOD.photoUrl,
        qualifications: editingHOD.qualifications,
        updatedAt: new Date()
      });

      alert('Profile updated successfully!');
      setShowHODProfileModal(false);
      loadHODData();
    } catch (error) {
      console.error('Error updating HOD profile:', error);
      alert('Failed to update profile');
    }
  };

  const handleHODPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    console.log('📤 Uploading HOD photo...', file.name);

    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('rollNumber', 'HOD');
      if (editingHOD?.photoUrl) {
        formData.append('oldPhotoUrl', editingHOD.photoUrl);
      }

      console.log('🌐 Sending request to server...');

      const response = await fetch(`${BACKEND_URL}/api/upload/hod-photo`, {
        method: 'POST',
        body: formData
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Upload successful:', data);
        
        const photoUrl = data.url || data.fileUrl || data.publicUrl;
        
        setEditingHOD({...editingHOD, photoUrl: photoUrl});
        
        const hodRef = doc(db, 'users', hod.email);
        await updateDoc(hodRef, {
          photoUrl: photoUrl,
          photoUpdatedAt: new Date()
        });
        
        alert('Photo uploaded successfully!');
        loadHODData();
      } else {
        const errorData = await response.json();
        console.error('❌ Server error:', errorData);
        alert(`Upload failed: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      alert(`Error uploading photo: ${error.message}`);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newStaff.email,
        newStaff.password
      );

      await setDoc(doc(db, 'users', newStaff.email), {
        email: newStaff.email,
        name: newStaff.name,
        role: newStaff.role,
        mobile: newStaff.mobile,
        qualifications: newStaff.qualifications,
        photoUrl: newStaff.photoUrl,
        department: 'Engineering Sciences & Humanities',
        status: 'active',
        staffId: `STAFF${Date.now()}`,
        assignedBatch: newStaff.role === 'mentor' ? newStaff.assignedBatch : null,
        assignedDivision: newStaff.role === 'class_coordinator' ? newStaff.assignedDivision : null,
        assignedStudents: [],
        activityLog: [
          {
            action: 'Staff Account Created',
            timestamp: new Date(),
            by: hod.email
          }
        ],
        createdAt: new Date(),
        createdBy: hod.email
      });

      alert('Staff member added successfully!');
      setShowAddStaffModal(false);
      setNewStaff({
        name: '',
        email: '',
        password: '',
        role: '',
        mobile: '',
        qualifications: '',
        photoUrl: '',
        assignedBatch: '',
        assignedDivision: ''
      });
      loadHODData();

      await signOut(auth);
      window.location.reload();
    } catch (error) {
      console.error('Error adding staff:', error);
      alert(`Failed to add staff: ${error.message}`);
    }
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();

    try {
      const staffRef = doc(db, 'users', editingStaff.email);
      const updateData = {
        name: editingStaff.name,
        mobile: editingStaff.mobile,
        qualifications: editingStaff.qualifications,
        photoUrl: editingStaff.photoUrl,
        role: editingStaff.role,
        assignedBatch: editingStaff.role === 'mentor' ? editingStaff.assignedBatch : null,
        assignedDivision: editingStaff.role === 'class_coordinator' ? editingStaff.assignedDivision : null,
        updatedAt: new Date(),
        updatedBy: hod.email
      };

      await updateDoc(staffRef, updateData);

      const staffDoc = await getDoc(staffRef);
      const currentLog = staffDoc.data().activityLog || [];
      await updateDoc(staffRef, {
        activityLog: [
          ...currentLog,
          {
            action: 'Profile Updated by HOD',
            timestamp: new Date(),
            by: hod.email
          }
        ]
      });

      alert('Staff updated successfully!');
      setShowEditStaffModal(false);
      setEditingStaff(null);
      loadHODData();
    } catch (error) {
      console.error('Error updating staff:', error);
      alert('Failed to update staff');
    }
  };

  const handleDeleteStaff = async (staffEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${staffEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', staffEmail));
      alert('Staff member deleted successfully');
      loadHODData();
    } catch (error) {
      console.error('Error deleting staff:', error);
      alert('Failed to delete staff member');
    }
  };

  const handleAssignment = async () => {
    try {
      if (assignmentMode === 'individual') {
        if (assignmentType === 'student') {
          const studentRef = doc(db, 'students', assignmentData.rollNumber);
          const studentDoc = await getDoc(studentRef);
          
          if (!studentDoc.exists()) {
            alert('Student not found!');
            return;
          }

          await updateDoc(studentRef, {
            division: assignmentData.division || studentDoc.data().division,
            batch: assignmentData.batch || studentDoc.data().batch,
            mentorEmail: assignmentData.mentorEmail || studentDoc.data().mentorEmail,
            classCoordinator: assignmentData.classCoordinator || studentDoc.data().classCoordinator,
            updatedAt: new Date(),
            updatedBy: hod.email
          });
          
          alert('Student assigned successfully!');
        } else if (assignmentType === 'mentor') {
          const mentorRef = doc(db, 'users', assignmentData.mentorEmail);
          await updateDoc(mentorRef, {
            assignedBatch: assignmentData.batch,
            updatedAt: new Date()
          });
          alert('Mentor assigned successfully!');
        } else if (assignmentType === 'coordinator') {
          const coordinatorRef = doc(db, 'users', assignmentData.coordinatorEmail);
          await updateDoc(coordinatorRef, {
            assignedDivision: assignmentData.division,
            updatedAt: new Date()
          });
          alert('Class Coordinator assigned successfully!');
        }
      } else {
        for (const rollNumber of selectedStudents) {
          const studentRef = doc(db, 'students', rollNumber);
          const studentDoc = await getDoc(studentRef);
          
          if (studentDoc.exists()) {
            const updateData = {
              updatedAt: new Date(),
              updatedBy: hod.email
            };
            
            if (assignmentData.division) updateData.division = assignmentData.division;
            if (assignmentData.batch) updateData.batch = assignmentData.batch;
            if (assignmentData.mentorEmail) updateData.mentorEmail = assignmentData.mentorEmail;
            if (assignmentData.classCoordinator) updateData.classCoordinator = assignmentData.classCoordinator;
            
            await updateDoc(studentRef, updateData);
          }
        }
        alert(`${selectedStudents.length} students assigned successfully!`);
      }

      setShowAssignmentModal(false);
      setSelectedStudents([]);
      setAssignmentData({});
      loadHODData();
    } catch (error) {
      console.error('Error in assignment:', error);
      alert('Assignment failed: ' + error.message);
    }
  };

  const getMessageTitle = (type) => {
    switch(type) {
      case 'warning': return 'Warning from HOD';
      case 'appreciation': return 'Appreciation from HOD';
      case 'reminder': return 'Reminder from HOD';
      case 'announcement': return 'Announcement from HOD';
      default: return 'Message from HOD';
    }
  };

  const getMessageIcon = (type) => {
    switch(type) {
      case 'warning': return '⚠️';
      case 'appreciation': return '🌟';
      case 'reminder': return '📢';
      case 'announcement': return '📝';
      default: return '📩';
    }
  };

  const handleSendMessage = async () => {
  if (!message.text || message.text.length > 100) {
    alert('Message must be between 1-100 characters');
    return;
  }

  try {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 8);

    const messageData = {
      from: hod.email,
      fromName: hod.name,
      messageType: message.messageType,
      title: getMessageTitle(message.messageType),
      message: message.text,
      icon: getMessageIcon(message.messageType),
      isRead: false,
      createdAt: Timestamp.now(),
      expiresAt: Timestamp.fromDate(expiresAt)
    };

    const recipientId = message.recipientType === 'student' 
      ? message.recipient.toUpperCase() 
      : message.recipient;
    
    console.log('📤 Sending message to:', recipientId);
    
    // Send to recipient's notifications
    await addDoc(collection(db, 'notifications', recipientId, 'messages'), messageData);
    console.log('✅ Message sent to notifications');

    // Save to sent messages collection
    const sentMessageData = {
      ...messageData,
      toEmail: message.recipient,
      toType: message.recipientType,
      fromEmail: hod.email
    };
    
    console.log('💾 Saving to sentMessages:', sentMessageData);
    const sentDocRef = await addDoc(collection(db, 'sentMessages'), sentMessageData);
    console.log('✅ Saved to sentMessages with ID:', sentDocRef.id);

    alert('Message sent successfully!');
    setShowMessagingModal(false);
    setMessage({
      recipient: '',
      recipientType: 'student',
      messageType: 'reminder',
      text: ''
    });
    
    // Reload HOD data
    await loadHODData();
  } catch (error) {
    console.error('❌ Error sending message:', error);
    alert('Failed to send message: ' + error.message);
  }
};

  const handleViewStaffProfile = async (staffEmail) => {
    try {
      const staffDoc = await getDoc(doc(db, 'users', staffEmail));
      if (staffDoc.exists()) {
        const staffData = staffDoc.data();
        
        let assignedStudents = [];
        if (staffData.assignedBatch) {
          assignedStudents = allStudents.filter(s => s.batch === staffData.assignedBatch);
        } else if (staffData.assignedDivision) {
          assignedStudents = allStudents.filter(s => s.division === staffData.assignedDivision);
        }

        setSelectedStaffProfile({
          ...staffData,
          assignedStudents
        });
        setShowStaffProfileModal(true);
      }
    } catch (error) {
      console.error('Error loading staff profile:', error);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const getBatchAttendanceData = () => {
    const batches = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'];
    return batches.map(batch => {
      const batchStudents = allStudents.filter(s => s.batch === batch);
      const avgAttendance = batchStudents.length > 0
        ? Math.round(
            batchStudents.reduce((sum, s) => sum + (s.attendance?.overallPercentage || 0), 0) /
            batchStudents.length
          )
        : 0;
      return { batch, attendance: avgAttendance };
    });
  };

  const sheetUrls = {
    attendance: 'https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/edit#gid=0',
    students: 'https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/edit#gid=1',
    iaTest1: 'https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/edit#gid=2',
    iaTest2: 'https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/edit#gid=3'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div 
                  className="w-12 h-12 rounded-full overflow-hidden bg-blue-600 cursor-pointer hover:ring-4 hover:ring-blue-300 transition-all"
                  onClick={() => {
                    setEditingHOD(hod);
                    setShowHODProfileModal(true);
                  }}
                  title="Click to edit your profile"
                >
                  {hod?.photoUrl ? (
                    <img src={hod.photoUrl} alt="HOD" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white">
                      <User className="w-6 h-6" />
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 border-2 border-white">
                  <Camera className="w-3 h-3 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">HOD Dashboard</h1>
                <p className="text-sm text-gray-600">
                  {hod?.name} | Department of First Year Engineering
                  <button
                    onClick={() => {
                      setEditingHOD(hod);
                      setShowHODProfileModal(true);
                    }}
                    className="ml-2 text-blue-600 hover:underline text-xs"
                  >
                    Edit Profile
                  </button>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowSheetsModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Grid className="w-4 h-4" />
                <span>View Sheets</span>
              </button>
              <button
                onClick={() => setShowAssignmentModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Users className="w-4 h-4" />
                <span>Allocation</span>
              </button>
              <button
                onClick={() => setShowMessagingModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Mail className="w-4 h-4" />
                <span>Send Message</span>
              </button>
              <button
                onClick={() => setShowSentMessages(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Send className="w-4 h-4" />
                <span>Sent ({sentMessages.length})</span>
              </button>
              <button
                onClick={() => setShowAddStaffModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4" />
                <span>Add Staff</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Student Search</h2>
          
          <div className="flex space-x-3 mb-3">
            <button
              onClick={() => {
                setSearchMode('roll');
                setShowSuggestions(false);
                setSearchSuggestions([]);
              }}
              className={`px-4 py-2 rounded-lg font-semibold ${
                searchMode === 'roll'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Search by Roll Number
            </button>
            <button
              onClick={() => {
                setSearchMode('name');
                setShowSuggestions(false);
                setSearchSuggestions([]);
              }}
              className={`px-4 py-2 rounded-lg font-semibold ${
                searchMode === 'name'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Search by Name
            </button>
          </div>

          <div className="flex space-x-3 relative">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchInputChange(searchMode === 'roll' ? e.target.value.toUpperCase() : e.target.value)}
                placeholder={searchMode === 'roll' ? 'Enter Roll Number (e.g., FB107)' : 'Enter First Name or Surname (e.g., Sawant)'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleSearchStudent()}
                onFocus={() => searchMode === 'name' && searchQuery.length >= 2 && setShowSuggestions(true)}
              />
              
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                  {searchSuggestions.map((student) => (
                    <div
                      key={student.rollNumber}
                      onClick={() => {
                        setSearchQuery(`${student.firstName} ${student.surname}`);
                        handleSearchStudent(student);
                      }}
                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {student.firstName} {student.surname}
                          </p>
                          <p className="text-sm text-gray-500">
                            {student.rollNumber} • Batch: {student.batch}
                          </p>
                        </div>
                        <Search className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <button
              onClick={() => handleSearchStudent()}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Search className="w-5 h-5" />
              <span>Search</span>
            </button>
          </div>

          {searchedStudent && (
            <div className="mt-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {searchedStudent.firstName} {searchedStudent.surname}
                  </h3>
                  <p className="text-gray-600">{searchedStudent.rollNumber} | Batch: {searchedStudent.batch}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => window.open(createStudentViewPath('/student/dashboard', searchedStudent.rollNumber), '_blank')}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>View Dashboard</span>
                  </button>
                  <button
                    onClick={() => setSearchedStudent(null)}
                    className="text-blue-600 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded">
                  <p className="text-sm text-gray-600">Attendance</p>
                  <p className={`text-2xl font-bold ${
                    (searchedStudent.attendance?.overallPercentage || 0) >= 75 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {(searchedStudent.attendance?.overallPercentage || 0).toFixed(2)}%
                  </p>
                </div>
                <div className="bg-white p-4 rounded">
                  <p className="text-sm text-gray-600">IA Test 1</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(searchedStudent.iaTest1?.percentage || 0).toFixed(2)}%
                  </p>
                </div>
                <div className="bg-white p-4 rounded">
                  <p className="text-sm text-gray-600">IA Test 2</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {(searchedStudent.iaTest2?.percentage || 0).toFixed(2)}%
                  </p>
                </div>
                <div className="bg-white p-4 rounded">
                  <p className="text-sm text-gray-600">Achievements</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {searchedStudent.achievements?.filter(a => a.status === 'verified').length || 0}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600">Total Students</h3>
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalStudents}</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600">Avg Attendance</h3>
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.avgAttendance}%</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600">At Risk</h3>
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-red-600">{stats.atRisk}</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-gray-600">Achievements</h3>
              <Award className="w-6 h-6 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalAchievements}</p>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Batch-wise Attendance</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={getBatchAttendanceData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="batch" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="attendance" fill="#3B82F6" name="Attendance %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Staff Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Staff Management</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allStaff.filter(s => s.role !== 'hod').map(staff => (
                  <tr key={staff.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {staff.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 hover:underline cursor-pointer"
                        onClick={() => handleViewStaffProfile(staff.email)}>
                      {staff.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {staff.role?.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {staff.assignedBatch || staff.assignedDivision || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                      <button
                        onClick={() => {
                          setEditingStaff(staff);
                          setShowEditStaffModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="w-4 h-4 inline" /> Edit
                      </button>
                      <button
                        onClick={() => handleViewStaffProfile(staff.email)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <Eye className="w-4 h-4 inline" /> View
                      </button>
                      <button
                        onClick={() => handleDeleteStaff(staff.email)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4 inline" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Google Sheets Modal */}
      {showSheetsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] m-4 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold">Google Sheets</h2>
              <button onClick={() => setShowSheetsModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex border-b">
              {['attendance', 'students', 'iaTest1', 'iaTest2'].map(sheet => (
                <button
                  key={sheet}
                  onClick={() => setActiveSheetTab(sheet)}
                  className={`px-6 py-3 font-semibold ${
                    activeSheetTab === sheet
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {sheet === 'iaTest1' ? 'IA Test 1' : sheet === 'iaTest2' ? 'IA Test 2' : sheet.charAt(0).toUpperCase() + sheet.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="flex-1 p-4">
              <iframe
                src={sheetUrls[activeSheetTab]}
                className="w-full h-full border-0"
                title={`${activeSheetTab} Sheet`}
              />
            </div>
            
            <div className="p-4 bg-gray-50 text-sm text-gray-600">
              <p>📝 <strong>Note:</strong> Replace the URLs in the code with your actual Google Sheets publish URLs</p>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">Allocation Management</h2>
              <button onClick={() => setShowAssignmentModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Mode</label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setAssignmentMode('individual');
                      setSelectedStudents([]);
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold ${
                      assignmentMode === 'individual'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Individual Assignment
                  </button>
                  <button
                    onClick={() => setAssignmentMode('bulk')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold ${
                      assignmentMode === 'bulk'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Bulk Assignment
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Type</label>
                <select
                  value={assignmentType}
                  onChange={(e) => setAssignmentType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="student">Assign Students to Division/Batch</option>
                  <option value="mentor">Assign Mentor to Batch</option>
                  <option value="coordinator">Assign Class Coordinator to Division</option>
                </select>
              </div>

              {assignmentMode === 'individual' && assignmentType === 'student' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Student Roll Number *</label>
                    <input
                      type="text"
                      value={assignmentData.rollNumber || ''}
                      onChange={(e) => setAssignmentData({...assignmentData, rollNumber: e.target.value.toUpperCase()})}
                      placeholder="e.g., FB101"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Division</label>
                      <select
                        value={assignmentData.division || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, division: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        <option value="A">Division A</option>
                        <option value="B">Division B</option>
                        <option value="C">Division C</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Batch</label>
                      <select
                        value={assignmentData.batch || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, batch: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        {['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'].map(batch => (
                          <option key={batch} value={batch}>{batch}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mentor Email</label>
                      <select
                        value={assignmentData.mentorEmail || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, mentorEmail: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        {allStaff.filter(s => s.role === 'mentor').map(mentor => (
                          <option key={mentor.email} value={mentor.email}>{mentor.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Class Coordinator</label>
                      <select
                        value={assignmentData.classCoordinator || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, classCoordinator: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        {allStaff.filter(s => s.role === 'class_coordinator').map(coord => (
                          <option key={coord.email} value={coord.name}>{coord.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {assignmentMode === 'bulk' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Students ({selectedStudents.length} selected)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded font-semibold">
                          <input
                            type="checkbox"
                            checked={selectedStudents.length === allStudents.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStudents(allStudents.map(s => s.rollNumber));
                              } else {
                                setSelectedStudents([]);
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Select All</span>
                        </label>
                        {allStudents.map(student => (
                          <label key={student.rollNumber} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={selectedStudents.includes(student.rollNumber)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStudents([...selectedStudents, student.rollNumber]);
                                } else {
                                  setSelectedStudents(selectedStudents.filter(r => r !== student.rollNumber));
                                }
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{student.rollNumber} - {student.firstName} {student.surname}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assign Division</label>
                      <select
                        value={assignmentData.division || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, division: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        <option value="A">Division A</option>
                        <option value="B">Division B</option>
                        <option value="C">Division C</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assign Batch</label>
                      <select
                        value={assignmentData.batch || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, batch: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        {['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'].map(batch => (
                          <option key={batch} value={batch}>{batch}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assign Mentor</label>
                      <select
                        value={assignmentData.mentorEmail || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, mentorEmail: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        {allStaff.filter(s => s.role === 'mentor').map(mentor => (
                          <option key={mentor.email} value={mentor.email}>{mentor.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assign Coordinator</label>
                      <select
                        value={assignmentData.classCoordinator || ''}
                        onChange={(e) => setAssignmentData({...assignmentData, classCoordinator: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keep Current</option>
                        {allStaff.filter(s => s.role === 'class_coordinator').map(coord => (
                          <option key={coord.email} value={coord.name}>{coord.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {assignmentType === 'mentor' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Mentor *</label>
                    <select
                      value={assignmentData.mentorEmail || ''}
                      onChange={(e) => setAssignmentData({...assignmentData, mentorEmail: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Mentor</option>
                      {allStaff.filter(s => s.role === 'mentor').map(mentor => (
                        <option key={mentor.email} value={mentor.email}>{mentor.name} ({mentor.email})</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Batch *</label>
                    <select
                      value={assignmentData.batch || ''}
                      onChange={(e) => setAssignmentData({...assignmentData, batch: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Batch</option>
                      {['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'].map(batch => (
                        <option key={batch} value={batch}>{batch}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {assignmentType === 'coordinator' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Class Coordinator *</label>
                    <select
                      value={assignmentData.coordinatorEmail || ''}
                      onChange={(e) => setAssignmentData({...assignmentData, coordinatorEmail: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Coordinator</option>
                      {allStaff.filter(s => s.role === 'class_coordinator').map(coord => (
                        <option key={coord.email} value={coord.email}>{coord.name} ({coord.email})</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Division *</label>
                    <select
                      value={assignmentData.division || ''}
                      onChange={(e) => setAssignmentData({...assignmentData, division: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Division</option>
                      <option value="A">Division A</option>
                      <option value="B">Division B</option>
                      <option value="C">Division C</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleAssignment}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Confirm Assignment
                </button>
                <button
                  onClick={() => {
                    setShowAssignmentModal(false);
                    setSelectedStudents([]);
                    setAssignmentData({});
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messaging Modal */}
      {showMessagingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl m-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">Send Message</h2>
              <button onClick={() => setShowMessagingModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Type</label>
                <select
                  value={message.recipientType}
                  onChange={(e) => setMessage({...message, recipientType: e.target.value, recipient: ''})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="student">Student</option>
                  <option value="staff">Staff</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient {message.recipientType === 'student' ? 'Roll Number' : 'Email'}
                </label>
                {message.recipientType === 'student' ? (
                  <select
                    value={message.recipient}
                    onChange={(e) => setMessage({...message, recipient: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Student</option>
                    {allStudents.map(student => (
                      <option key={student.rollNumber} value={student.rollNumber}>
                        {student.rollNumber} - {student.firstName} {student.surname}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={message.recipient}
                    onChange={(e) => setMessage({...message, recipient: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Staff</option>
                    {allStaff.filter(s => s.role !== 'hod').map(staff => (
                      <option key={staff.email} value={staff.email}>
                        {staff.name} ({staff.email})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message Type</label>
                <select
                  value={message.messageType}
                  onChange={(e) => setMessage({...message, messageType: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="warning">⚠️ Warning</option>
                  <option value="appreciation">🌟 Appreciation</option>
                  <option value="reminder">📢 Reminder</option>
                  <option value="announcement">📝 Announcement</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message ({message.text.length}/100 characters)
                </label>
                <textarea
                  value={message.text}
                  onChange={(e) => setMessage({...message, text: e.target.value})}
                  maxLength={100}
                  rows={4}
                  placeholder="Type your message here (max 100 characters)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Message will be automatically deleted after 8 days
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleSendMessage}
                  disabled={!message.recipient || !message.text || message.text.length > 100}
                  className="flex-1 flex items-center justify-center space-x-2 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  <span>Send Message</span>
                </button>
                <button
                  onClick={() => {
                    setShowMessagingModal(false);
                    setMessage({
                      recipient: '',
                      recipientType: 'student',
                      messageType: 'reminder',
                      text: ''
                    });
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HOD Profile Edit Modal */}
      {showHODProfileModal && editingHOD && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">Edit Your Profile</h2>
              <button onClick={() => setShowHODProfileModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateHODProfile} className="p-6 space-y-4">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-blue-600">
                    {editingHOD.photoUrl ? (
                      <img src={editingHOD.photoUrl} alt="HOD" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 bg-blue-600 p-2 rounded-full cursor-pointer hover:bg-blue-700">
                    <Camera className="w-5 h-5 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHODPhotoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={editingHOD.name}
                  onChange={(e) => setEditingHOD({...editingHOD, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email (Cannot be changed)</label>
                <input
                  type="email"
                  value={editingHOD.email}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                <input
                  type="tel"
                  value={editingHOD.mobile || ''}
                  onChange={(e) => setEditingHOD({...editingHOD, mobile: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Photo URL</label>
                <input
                  type="url"
                  value={editingHOD.photoUrl || ''}
                  onChange={(e) => setEditingHOD({...editingHOD, photoUrl: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Qualifications</label>
                <textarea
                  value={editingHOD.qualifications || ''}
                  onChange={(e) => setEditingHOD({...editingHOD, qualifications: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Ph.D. in Engineering, M.Tech, B.E."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setShowHODProfileModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Staff Profile Modal */}
      {showStaffProfileModal && selectedStaffProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">Staff Profile</h2>
              <button onClick={() => setShowStaffProfileModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex items-start space-x-6">
                <div className="w-32 h-32 rounded-lg border-4 border-blue-600 overflow-hidden bg-gray-100">
                  {selectedStaffProfile.photoUrl ? (
                    <img 
                      src={selectedStaffProfile.photoUrl} 
                      alt="Staff" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900">{selectedStaffProfile.name}</h3>
                  <p className="text-gray-600">{selectedStaffProfile.email}</p>
                  <p className="text-sm text-gray-500 mt-2 capitalize">{selectedStaffProfile.role?.replace('_', ' ')}</p>
                  
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Mobile</p>
                      <p className="font-semibold">{selectedStaffProfile.mobile || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Staff ID</p>
                      <p className="font-semibold">{selectedStaffProfile.staffId}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Qualifications</h4>
                <p className="text-gray-700">{selectedStaffProfile.qualifications || 'Not specified'}</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">
                  Assigned Students ({selectedStaffProfile.assignedStudents?.length || 0})
                </h4>
                {selectedStaffProfile.assignedStudents?.length > 0 ? (
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Roll No</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Batch</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Attendance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedStaffProfile.assignedStudents.map(student => (
                          <tr key={student.rollNumber}>
                            <td className="px-4 py-2 text-sm">{student.rollNumber}</td>
                            <td className="px-4 py-2 text-sm">{student.firstName} {student.surname}</td>
                            <td className="px-4 py-2 text-sm">{student.batch}</td>
                            <td className="px-4 py-2 text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                (student.attendance?.overallPercentage || 0) >= 75
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {(student.attendance?.overallPercentage || 0).toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No students assigned</p>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Activity Log</h4>
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {selectedStaffProfile.activityLog && selectedStaffProfile.activityLog.length > 0 ? (
                    <div className="divide-y">
                      {selectedStaffProfile.activityLog.slice().reverse().map((log, index) => (
                        <div key={index} className="p-3">
                          <p className="text-sm font-medium text-gray-900">{log.action}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {log.timestamp?.toDate ? new Date(log.timestamp.toDate()).toLocaleString() : 
                             log.timestamp instanceof Date ? log.timestamp.toLocaleString() : 'N/A'}
                            {log.by && ` • By: ${log.by}`}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No activity logged yet</p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowStaffProfileModal(false)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditStaffModal && editingStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-3xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">Edit Staff Member</h2>
              <button onClick={() => setShowEditStaffModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleEditStaff} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={editingStaff.name}
                    onChange={(e) => setEditingStaff({...editingStaff, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                  <input
                    type="tel"
                    value={editingStaff.mobile}
                    onChange={(e) => setEditingStaff({...editingStaff, mobile: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                  <select
                    value={editingStaff.role}
                    onChange={(e) => setEditingStaff({...editingStaff, role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="mentor">Mentor</option>
                    <option value="class_coordinator">Class Coordinator</option>
                    <option value="mentoring_coordinator">Mentoring Coordinator</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photo URL</label>
                  <input
                    type="url"
                    value={editingStaff.photoUrl || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, photoUrl: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://..."
                  />
                </div>

                {editingStaff.role === 'mentor' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Batch</label>
                    <select
                      value={editingStaff.assignedBatch || ''}
                      onChange={(e) => setEditingStaff({...editingStaff, assignedBatch: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not Assigned</option>
                      {['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'].map(batch => (
                        <option key={batch} value={batch}>{batch}</option>
                      ))}
                    </select>
                  </div>
                )}

                {editingStaff.role === 'class_coordinator' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Division</label>
                    <select
                      value={editingStaff.assignedDivision || ''}
                      onChange={(e) => setEditingStaff({...editingStaff, assignedDivision: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not Assigned</option>
                      <option value="A">Division A</option>
                      <option value="B">Division B</option>
                      <option value="C">Division C</option>
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Qualifications</label>
                <textarea
                  value={editingStaff.qualifications || ''}
                  onChange={(e) => setEditingStaff({...editingStaff, qualifications: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., M.Tech in Computer Science, B.E. in Electronics"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditStaffModal(false);
                    setEditingStaff(null);
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Staff Member</h2>
            
            <form onSubmit={handleAddStaff} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={newStaff.name}
                    onChange={(e) => setNewStaff({...newStaff, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    value={newStaff.email}
                    onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                  <input
                    type="password"
                    value={newStaff.password}
                    onChange={(e) => setNewStaff({...newStaff, password: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    minLength="6"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                  <input
                    type="tel"
                    value={newStaff.mobile}
                    onChange={(e) => setNewStaff({...newStaff, mobile: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                  <select
                    value={newStaff.role}
                    onChange={(e) => setNewStaff({...newStaff, role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Role</option>
                    <option value="mentor">Mentor</option>
                    <option value="class_coordinator">Class Coordinator</option>
                    <option value="mentoring_coordinator">Mentoring Coordinator</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photo URL</label>
                  <input
                    type="url"
                    value={newStaff.photoUrl}
                    onChange={(e) => setNewStaff({...newStaff, photoUrl: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://..."
                  />
                </div>

                {newStaff.role === 'mentor' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assign Batch *</label>
                    <select
                      value={newStaff.assignedBatch}
                      onChange={(e) => setNewStaff({...newStaff, assignedBatch: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Batch</option>
                      {['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'].map(batch => (
                        <option key={batch} value={batch}>{batch}</option>
                      ))}
                    </select>
                  </div>
                )}

                {newStaff.role === 'class_coordinator' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assign Division *</label>
                    <select
                      value={newStaff.assignedDivision}
                      onChange={(e) => setNewStaff({...newStaff, assignedDivision: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Division</option>
                      <option value="A">Division A</option>
                      <option value="B">Division B</option>
                      <option value="C">Division C</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Qualifications</label>
                <textarea
                  value={newStaff.qualifications}
                  onChange={(e) => setNewStaff({...newStaff, qualifications: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., M.Tech in Computer Science, B.E. in Electronics"
                />
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
                >
                  Add Staff Member
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddStaffModal(false);
                    setNewStaff({
                      name: '',
                      email: '',
                      password: '',
                      role: '',
                      mobile: '',
                      qualifications: '',
                      photoUrl: '',
                      assignedBatch: '',
                      assignedDivision: ''
                    });
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sent Messages Modal */}
      {showSentMessages && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold">Sent Messages ({sentMessages.length})</h2>
              <button onClick={() => setShowSentMessages(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {sentMessages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>No messages sent yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentMessages.map((msg) => (
                    <div key={msg.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className="text-3xl">{msg.icon}</span>
                          <div>
                            <p className="font-semibold text-gray-900">{msg.title}</p>
                            <p className="text-sm text-gray-500">
                              To: {msg.toEmail} ({msg.toType})
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">
                            {msg.createdAt?.toDate ? new Date(msg.createdAt.toDate()).toLocaleString() : 'Just now'}
                          </p>
                          <p className="text-xs text-red-500 mt-1">
                            Expires: {msg.expiresAt?.toDate ? new Date(msg.expiresAt.toDate()).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-700 mt-2 pl-12">{msg.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HODDashboard;
